import React, { useState, useCallback, useMemo, JSX } from 'react';
import './SearchComponent.scss';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import RMTable from 'components/customComponents/Controls/RMTable';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { ClipboardBuffer } from 'components/plateviewer/experiment/ClipboardBuffer';
import { ClipboardSearchResults } from 'business/models/CustomModels';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import 'components/main/userGroup/sections/style.scss';
import { SearchResultsDTO, SearchItemsTarget } from 'business/models/Models';
import SearchService from 'business/services/SearchService';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMSvgIcon, {
  RMSvgIconNames,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { LoadingModal } from 'components/modal';
import { useDeleteNodesHandler } from 'components/explorer/hooks';
import useRMLite from 'CustomHooks/useRMLite';

const handleTableFilter = (filterable: boolean, filterTitle: string) => (event: any) =>
  <TableSortInputFilter event={event} title={filterTitle} showSearchInput={filterable} />;

const EMPTY_DATE_SORT_VALUE = Number.POSITIVE_INFINITY;

const parseDatePreparedValue = (value: string | null | undefined): number => {
  if (!value) return EMPTY_DATE_SORT_VALUE;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? EMPTY_DATE_SORT_VALUE : time;
};

const sortDatePrepared = (rowA, rowB, columnId: string) => {
  const a = parseDatePreparedValue(rowA.values[columnId]);
  const b = parseDatePreparedValue(rowB.values[columnId]);
  return a === b ? 0 : a - b;
};

const documentTypes = [
  ETreeNodeTypes.Experiment,
  ETreeNodeTypes.Screen,
  ETreeNodeTypes.Protein,
  ETreeNodeTypes.Seed,
];

const NodesRoute = {
  Experiment: 'exp',
  Screen: 'screen',
  ProteinFormulation: 'protein',
  Seed: 'seed',
};

const getNodeLink = (treeNodeId, experimentId, treeNodeType) => {
  let type = treeNodeType;
  if (documentTypes.includes(treeNodeType as ETreeNodeTypes)) {
    type = NodesRoute[treeNodeType] || treeNodeType;
  }
  return `/${type}/${experimentId ? experimentId : treeNodeId}`;
};

const groupingColumns = ['type'];
const iconsColor = '#474747';
const iconsSize = {
  width: 15,
  height: 15,
};

const fileOpenHandler = (item: SearchResultsDTO) => () => {
  const link = getNodeLink(item.treeNodeId, item.experimentId, item.type);
  link && window.open(link, link);
};

const containerOpenHandler = (item: SearchResultsDTO) => () => {
  window.open(`/project/${item?.containerNodeid}`);
};

const getIconProps = (iconName: RMSvgIconNames) => {
  return {
    name: iconName,
    color: iconsColor,
    ...iconsSize,
  };
};

const getActionButtons = (item: SearchResultsDTO) => {
  const iconName: RMSvgIconNames = item?.type as RMSvgIconNames;
  return (
    <div
      id="searchcomponent-getactionbuttons-wrapper"
      className="flex flex-row w-full h-full"
    >
      <div
        id="searchcomponent-getactionbuttons-icon-type"
        className="flex w-1/2 h-full items-center"
      >
        <RMCustomButton
          mainContainerClass={
            documentTypes.includes(item.type as ETreeNodeTypes)
              ? 'flex align-items-start'
              : 'd-none'
          }
          iconProps={getIconProps(iconName)}
          onClick={fileOpenHandler(item)}
        />
      </div>
      <div
        id="searchcomponent-getactionbuttons-folder-open"
        className="flex w-1/2 h-full"
      >
        <RMCustomButton
          mainContainerClass="flex align-items-start"
          iconProps={getIconProps('FolderOpen')}
          onClick={containerOpenHandler(item)}
        />
      </div>
    </div>
  );
};

export default function SearchComponent() {
  const rmLite = useRMLite();
  const ClipBoardBuffer = new ClipboardBuffer();
  const [searchQuery, setSearchQuery] = useState('');
  const commonTargets = [
    { id: SearchItemsTarget.ProjectNFolder, value: 'Project/Folder' },
    { id: SearchItemsTarget.Experiment, value: 'Experiments' },
  ];
  const otherTargets = [
    { id: SearchItemsTarget.Screen, value: 'Screens' },
    { id: SearchItemsTarget.Protein, value: 'Protein' },
    { id: SearchItemsTarget.Seed, value: 'Seeds' },
  ];
  const searchTargetsArray = [...commonTargets, ...(rmLite ? [] : otherTargets)];
  const [searchTargets, setSearchTargets] = useState<SearchItemsTarget[]>(
    searchTargetsArray.map((target) => target.id),
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const deleteNodesHandler = useDeleteNodesHandler();
  const options: {
    accessor: string;
    Header: string;
    Filter?: (event: any) => JSX.Element;
    className?: string;
    sortType?: string | typeof sortDatePrepared;
  }[] = [
    {
      accessor: 'plateId',
      Header: 'Plate ID',
      Filter: handleTableFilter(true, 'Plate ID'),
    },
    {
      accessor: 'experimentId',
      Header: 'Exp. ID',
      Filter: handleTableFilter(true, 'Experiment ID'),
    },
    {
      accessor: 'projectName',
      Header: 'Project Name',
      Filter: handleTableFilter(true, 'Project Name'),
    },
    {
      accessor: 'fileName',
      Header: 'File Name',
      Filter: handleTableFilter(true, 'File Name'),
    },
    {
      accessor: 'plateName',
      Header: 'Plate Name',
      Filter: handleTableFilter(true, 'Plate Name'),
    },
    {
      accessor: 'owner',
      Header: 'Owner',
      Filter: handleTableFilter(true, 'Owner'),
    },
    {
      accessor: 'barcode',
      Header: 'Barcode',
      Filter: handleTableFilter(true, 'Barcode'),
    },

    {
      accessor: 'datePrepared',
      Header: 'Date Prepared',
      Filter: handleTableFilter(true, 'Date Prepared'),
      sortType: sortDatePrepared,
    },
    {
      accessor: 'action',
      Header: 'Action',
      className: 'max-w-[120px]',
    },
    {
      accessor: 'type',
      Header: 'Type',
    },
  ];
  const handleSearchTarget = useCallback(
    (searchTarget) => () => {
      const searchTargetArray = searchTargets;
      if (searchTargetArray.includes(searchTarget)) {
        searchTargetArray.remove(searchTarget);
      } else {
        searchTargetArray.push(searchTarget);
      }
      setSearchTargets([...searchTargetArray]);
    },
    [searchTargets],
  );

  const nodesDeletePostProcessor = (deleteNodeIds: number[]) => {
    if (deleteNodeIds?.length) {
      const searchResultsAfterDeletion = searchResults.filter((item) => {
        if (!deleteNodeIds.includes(item.treeNodeId)) {
          return item;
        }
      });
      setSearchResults([...searchResultsAfterDeletion]);
    }
    setSelectedRows([]);
  };
  const handleOnNodesDelete = async () => {
    await deleteNodesHandler(
      selectedRows.map((rowIndex) => ({
        treeNodeId: searchResults[rowIndex].treeNodeId,
        name: searchResults[rowIndex].fileName,
        type: searchResults[rowIndex].type as ETreeNodeTypes,
        entityId: parseInt(searchResults[rowIndex]?.experimentId),
      })),
      nodesDeletePostProcessor,
    );
  };
  const search = useCallback(async () => {
    setIsLoading(true);
    const searchService = new SearchService();
    const res: SearchResultsDTO[] = await searchService.Search(
      searchQuery,
      searchTargets,
    );

    if (res?.length) {
      const searchResultWithActionButtons = res.map((item) => {
        return {
          ...item,
          type: item.type === 'ProteinFormulation' ? 'Protein' : item.type,
          action: getActionButtons(item),
        };
      });
      setSearchResults(searchResultWithActionButtons);
      setSelectedRows([]);
    } else {
      clearSearchResults();
    }

    setIsLoading(false);
  }, [searchTargets, searchQuery]);

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedRows([...rowIndices]);
  }, []);

  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setSelectedRows([]);
  }, []);

  const handleSearchQueryChange = useCallback((e) => {
    setSearchQuery(e.target.value.trim());
  }, []);

  const handleInputKeyPress = useCallback(
    (e) => {
      if (e.key == 'Enter') {
        if (searchQuery.length > 2 && searchTargets.length) {
          e.preventDefault();
          search();
        }
      }
    },
    [searchQuery, searchTargets],
  );

  const renderSearchTargets = useMemo(
    () =>
      searchTargetsArray.map((searchTarget, index) => (
        <div
          id={`searchcomponent-target-${index}`}
          key={`search-target-${index}`}
          className="search-target"
        >
          <div id={`searchcomponent-target-${index}-checkbox-wrapper`}>
            <RMCheckBoxY
              isChecked={searchTargets.includes(searchTarget.id)}
              onChange={handleSearchTarget(searchTarget.id)}
              disabled={false}
            />
          </div>
          <div id={`searchcomponent-target-${searchTarget.value.toLowerCase()}-label`}>
            {searchTarget.value}{' '}
          </div>
        </div>
      )),
    [searchTargetsArray, searchTargets],
  );

  const copyRows = useCallback(async () => {
    const selectedData: ClipboardSearchResults[] = selectedRows.map((item) => {
      return {
        plateId: searchResults[item]?.plateId,
        experimentId: searchResults[item]?.experimentId,
        projectName: searchResults[item]?.projectName,
        fileName: searchResults[item]?.fileName,
        plateName: searchResults[item]?.plateName,
        owner: searchResults[item]?.owner,
        barcode: searchResults[item]?.barcode,
        datePrepared: searchResults[item]?.datePrepared,
      };
    });

    const copyResult = await ClipBoardBuffer.copySearchData(selectedData);
    if (copyResult) {
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'File(s) has been copied successfully',
      );
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Could not copy File(s) to clipboard',
      );
    }
  }, [selectedRows]);

  return (
    <div id="searchcomponent-main-container" className="main-container">
      <div id="searchcomponent-search-header" className="search-header">
        <div id='searchcomponent-search-icon-wrapper'>
          <RMSvgIcon
            id="searchcomponent-icon"
            name="RMLogo"
            width={'30px'}
            height={'30px'}
          />
        </div>
        <div id="searchcomponent-search-header-title" className="search-header-title">
          Search All
        </div>
      </div>

      <LoadingModal showModal={isLoading} text={'Please Wait...'} />
      <div id="searchcomponent-search-container" className="search-container">
        <div id="searchcomponent-main-row-container" className="main-row-container">
          <div id="searchcomponent-row-heading" className="row-heading">
            Search:
          </div>
          <div id="searchcomponent-row-content" className="row-content">
            <input
              id="searchcomponent-search-input"
              className="search-input"
              type="text"
              onChange={handleSearchQueryChange}
              onKeyPress={handleInputKeyPress}
            />
            <div id="searchcomponent-searching-button" className="searching-button">
              <RMCustomButton
                title="Search"
                contentContainerClass="btn-yellow-wide"
                onClick={search}
                isDisabled={searchQuery.trim()?.length === 0 || !searchTargets.length}
              />
            </div>
          </div>
        </div>
        <div id="searchcomponent-main-row-container" className="main-row-container">
          <div id="searchcomponent-row-heading" className="row-heading">
            Search Target:
          </div>

          <div id="searchcomponent-row-content" className="row-content">
            {renderSearchTargets}
          </div>
        </div>
      </div>

      <div
        id="searchcomponent-result-header-container"
        style={{
          marginLeft: '1vw',
          marginRight: '1vw',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <div id="searchcomponent-result-header-text">
          {' '}
          {`Search Result (${searchResults.length})`}
        </div>
        {!rmLite && (
          <div id="searchcomponent-rm-custom-button" className="d-flex flex-row ml-auto">
            <RMCustomButton
              iconProps={{ name: 'Delete', fill: '#000000' }}
              onClick={handleOnNodesDelete}
              isDisabled={!selectedRows.length}
            />

            <RMCustomButton
              contentContainerClass="ml-2vw"
              iconProps={{ name: 'Copy', fill: '#000000' }}
              onClick={copyRows}
              isDisabled={!selectedRows.length}
            />
          </div>
        )}
      </div>

      {searchResults?.length > 0 && (
        <div id="searchcomponent-result-container" className="result-container">
          <RMTable
            columns={options}
            data={searchResults}
            selectedIds={selectedRows}
            onCheckboxSelectionChange={!rmLite && handleSelectionChange}
            groupingColumns={groupingColumns}
            hiddenColumns={groupingColumns}
            tableClass="w-full"
          ></RMTable>
        </div>
      )}
    </div>
  );
}
